import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { SmlsModal, SmlsButton, SmlsDropzone } from '@smiles/smiles-ui-kit-react';
import { useTranslation } from 'react-i18next';
import { Services as UtilsServices } from '@smiles/core-utils';
import fileConverter from 'utils/fileConverter';
import { loginDataLayer } from 'utils/dataLayer';

type FilesUrls = {
  id: number;
  url?: string;
  error?: string;
};
interface Props {
  isOpen?: boolean;
  isLoading: boolean;
  onSubmit(data: FilesUrls[]): void;
  onBackButton(): void;
  onSkipButton(): void;
}

type UploadFile = {
  id: number;
  file: File;
};

type ErrorCodes = {
  [key: string]: string;
};

const UploadUserFilesModal: React.FC<Props> = ({ isOpen = false, isLoading, onSubmit, onBackButton, onSkipButton }) => {
  const [toggleUserForm, setToggleUserForm] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>([]);
  const [fileUrls, setFileUrls] = useState<FilesUrls[]>([]);
  const hasFilesWithError = useMemo(() => fileUrls.some((item) => item.error), [fileUrls]);
  const hasFilesWithoutUrl = useMemo(() => fileUrls.some((item) => !item.url), [fileUrls]);

  useEffect(() => {
    if (isOpen === true) {
      const step = toggleUserForm === true ? '/enviar' : '';
      loginDataLayer.triggerPageViewEvent(`conta-cancelada/documentos${step}`, 2);
    }
  }, [isOpen, toggleUserForm]);

  const getErrorByCode = useCallback((errorCode: string) => {
    const errors = {
      FILE_SIZE_ERROR: 'O arquivo deve ter menos de 5 MB.',
      FILE_FORMAT_ERROR: 'Formato de arquivo não permitido.',
      SERVICE_ERROR: 'Não foi possivel anexar o arquivo. Tente novamente.',
      DEFAULT: 'Não foi possivel anexar o arquivo. Tente novamente.',
    } as ErrorCodes;
    return errors[errorCode] || errors.DEFAULT;
  }, []);

  const { t } = useTranslation('upload_user_files');

  const handleSubmitForm = useCallback(
    async (e: React.FormEvent<EventTarget>) => {
      e.preventDefault();
      onSubmit(fileUrls);
    },
    [fileUrls, onSubmit],
  );

  const removeItemFromFiles = useCallback(
    (fileId: number) => {
      const newArrayOfFiles = selectedFiles.filter((file) => file.id !== fileId);
      const newArrayOfUrls = fileUrls.filter((file) => file.id !== fileId);
      setSelectedFiles([...newArrayOfFiles]);
      setFileUrls(newArrayOfUrls);
    },
    [selectedFiles, fileUrls],
  );

  const handleBackButton = useCallback(() => {
    if (!toggleUserForm) {
      onBackButton();
      return;
    }
    setToggleUserForm(false);
  }, [onBackButton, toggleUserForm]);

  const handleUploadFile = useCallback(async (fileToUpload: UploadFile) => {
    const { id, file } = fileToUpload;
    const allowedMimetypes = ['application/pdf', 'image/jpeg', 'image/png'];
    const MbFileSize = file.size / 1000000;

    if (MbFileSize > 5) {
      setFileUrls((oldValues) => [
        ...oldValues,
        {
          id,
          error: 'FILE_SIZE_ERROR',
        },
      ]);
      return;
    }

    if (!allowedMimetypes.includes(file.type)) {
      setFileUrls((oldValues) => [
        ...oldValues,
        {
          id,
          error: 'FILE_FORMAT_ERROR',
        },
      ]);
      return;
    }

    const [_, extension] = file.type.split('/');
    const base64File = await fileConverter(file);

    const { success, data } = await UtilsServices.uploadFileS3({
      extension,
      file: base64File,
      category: 'ACCOUNT_CANCELED',
    });

    const isSuccessfulUpload = success && data && data.fileUrl;

    const fileResponse = {
      id,
      error: !isSuccessfulUpload ? 'SERVICE_ERROR' : undefined,
      url: isSuccessfulUpload ? data.fileUrl : undefined,
    };

    setFileUrls((oldValues) => [...oldValues, fileResponse]);
  }, []);

  const retryUploadFile = useCallback(
    (fileId: number) => {
      setFileUrls((oldArray) => oldArray.filter((item) => item.id !== fileId));
      const file = selectedFiles.find((item) => item.id === fileId);

      if (!file) return;
      handleUploadFile(file);
    },
    [selectedFiles, handleUploadFile],
  );

  const handleAddFile = useCallback(
    (files: File[]) => {
      const allowedNumberOfFiles = 3 - selectedFiles.length;
      const formattedFiles = files.slice(0, allowedNumberOfFiles).map((fileData: File) => {
        const formattedFile = { id: Math.round(Date.now() * Math.random()), file: fileData };
        handleUploadFile(formattedFile);
        return formattedFile;
      });
      setSelectedFiles((oldFiles) => {
        return [...oldFiles, ...formattedFiles];
      });
    },
    [selectedFiles, handleUploadFile],
  );

  const getUploadStatus = useCallback(
    (id: number) => {
      const findFile = fileUrls.find((file) => file.id === id);

      if (!findFile?.url && findFile?.error) {
        return findFile.error === 'SERVICE_ERROR' ? (
          <button type="button" onClick={() => retryUploadFile(id)}>
            <i className="material-icons">refresh</i>
          </button>
        ) : (
          <button type="button" onClick={() => removeItemFromFiles(id)}>
            <i className="material-icons">close</i>
          </button>
        );
      }

      if (findFile?.url) {
        return (
          <button type="button" onClick={() => removeItemFromFiles(id)}>
            <i className="material-icons">close</i>
          </button>
        );
      }
      return <div className="file-loader" />;
    },
    [fileUrls, retryUploadFile, removeItemFromFiles],
  );

  const getSizeOrError = useCallback(
    (id: number) => {
      const selectedFile = selectedFiles.find((item) => item.id === id);
      const uploadedFile = fileUrls.find((item) => item.id === id);

      if ((selectedFile && !uploadedFile) || (selectedFile && uploadedFile && uploadedFile.url)) {
        const KBnumber = selectedFile.file.size / 1000;
        if (KBnumber < 1000) {
          return `${KBnumber.toFixed(2)} KB`;
        }
        return <span>{`${(KBnumber / 1000).toFixed(2)} MB`}</span>;
      }
      return (
        <span className="error">
          <i className="material-icons">error_outline</i>
          {getErrorByCode(uploadedFile?.error || 'DEFAULT')}
        </span>
      );
    },
    [selectedFiles, fileUrls, getErrorByCode],
  );

  const getFormattedFileName = useCallback((fileName: string) => {
    if (fileName.length < 27) return fileName;
    const [name, ext] = fileName.split('.');
    return `${name.substring(0, 20)}...${ext}`;
  }, []);

  return (
    <SmlsModal id="uploadModal" isOpen={isOpen} defaultHeader={false} className="upload-docs-modal" type="right">
      <>
        <div className="modal-header">
          <button type="button" className="smls-btn-close" aria-label="Close" onClick={handleBackButton}>
            <i className="material-icons">keyboard_arrow_left</i>
          </button>
        </div>
        {!toggleUserForm ? (
          <div className="wellcome-message">
            <h4>{t('title')}</h4>
            <p>{t('subtitle')}</p>

            <SmlsButton
              text={t('fill_form_button')}
              color="primary"
              type="button"
              disabled={false}
              onClick={() => setToggleUserForm(true)}
            />
            <SmlsButton text={t('skip_form')} color="hyperlink" onClick={onSkipButton} />
          </div>
        ) : (
          <form className="upload-files-form" onSubmit={handleSubmitForm}>
            <h4>{t('form_title')}</h4>
            <p>{t('form_subtitle')}</p>
            {selectedFiles.length > 0 && (
              <ul>
                {selectedFiles.map(({ id, file }) => (
                  <li key={id} className="selected-file-item">
                    <div>
                      <p>{getFormattedFileName(file.name)}</p>
                      {getSizeOrError(id)}
                    </div>
                    {getUploadStatus(id)}
                  </li>
                ))}
              </ul>
            )}
            {selectedFiles.length < 3 && (
              <SmlsDropzone
                collapsed={selectedFiles.length > 0}
                textDesktop={t('form_dropzone_text')}
                textMobile={t('form_dropzone_mobile_text')}
                cbOnFilesAdded={handleAddFile}
              />
            )}
            {selectedFiles.length > 0 && (
              <SmlsButton
                text={t('form_submit_button')}
                type="submit"
                color="primary"
                isLoading={isLoading}
                disabled={hasFilesWithError || hasFilesWithoutUrl || selectedFiles.length !== fileUrls.length}
              />
            )}
          </form>
        )}
      </>
    </SmlsModal>
  );
};

export default UploadUserFilesModal;
